import { seitenAufteilung } from "./bedrock.ts";
import { blaetterSlots, slotAnzahl } from "./form.ts";
import { zerlege } from "./minimessage.ts";
import type { Katalog, KatalogQuelle, Listenbereich, SlotFelder, SlotUeberschreibung, UiDaten } from "./modell.ts";
import { istMehrzeilenZeile, platzhalterIn } from "./platzhalter.ts";

export type Befund = { stufe: "fehler" | "warnung"; ort: string; meldung: string; slot?: number };
export type Umfeld = { uiExistiert: (id: string) => boolean };

export function hatFehler(b: Befund[]): boolean {
  return b.some((x) => x.stufe === "fehler");
}

const IMMER_BEKANNT = ["seite", "seiten"];
const HAT_PLATZHALTER = /\{[a-z0-9_]+(\.\.\.)?\}/;

type Melder = (stufe: Befund["stufe"], meldung: string) => void;

/** Prüft Platzhalter und MiniMessage in Text-Feldern; `bekannt` null heißt: Quelle unbekannt, nicht prüfen. */
function pruefeTexte(f: Partial<SlotFelder>, bekannt: Set<string> | null, rollen: Record<string, string>, melde: Melder): void {
  const einzeilig = [f.symbol, f.name, f.wert, f.bestaetigung, typeof f.anzahl === "string" ? f.anzahl : undefined].filter((x) => x !== undefined);
  const zeilen = f.beschreibung ?? [];
  pruefeZeilen(einzeilig, zeilen, bekannt, melde);
  for (const t of [f.name, f.bestaetigung, ...zeilen]) {
    if (t === undefined) continue;
    for (const b of zerlege(t, rollen).befunde) melde("warnung", b.meldung);
  }
}

function pruefeZeilen(einzeilig: string[], zeilen: string[], bekannt: Set<string> | null, melde: Melder): void {
  for (const t of einzeilig) if (platzhalterIn(t).some((p) => p.mehrzeilig)) melde("warnung", "Mehrzeiliger Platzhalter muss allein in der Zeile stehen.");
  for (const z of zeilen) if (platzhalterIn(z).some((p) => p.mehrzeilig) && !istMehrzeilenZeile(z)) melde("warnung", "Mehrzeiliger Platzhalter muss allein in der Zeile stehen.");
  if (!bekannt) return;
  const gemeldet = new Set<string>();
  for (const t of [...einzeilig, ...zeilen]) {
    for (const p of platzhalterIn(t)) {
      if (bekannt.has(p.name) || gemeldet.has(p.name)) continue;
      gemeldet.add(p.name);
      melde("warnung", `Platzhalter {${p.name}} liefert die Quelle nicht.`);
    }
  }
}

function pruefeAktion(f: Partial<SlotFelder>, katalog: Katalog, umfeld: Umfeld, melde: Melder): void {
  if (!f.aktion) return;
  const aktion = katalog.aktionen.find((a) => a.schluessel === f.aktion);
  if (!aktion) {
    melde("warnung", `Aktion „${f.aktion}“ ist im Katalog nicht bekannt.`);
    return;
  }
  if (aktion.argument === "keins") return;
  if (!f.wert) {
    melde("fehler", "Aktion braucht ein Argument.");
    return;
  }
  if (HAT_PLATZHALTER.test(f.wert)) return;
  if (aktion.argument === "ui" && !umfeld.uiExistiert(f.wert)) melde("fehler", "Verweis auf eine UI, die es nicht mehr gibt.");
  if (typeof aktion.argument === "object" && !aktion.argument.werte.includes(f.wert)) melde("fehler", "Argument ist für diese Aktion nicht erlaubt.");
}

export function pruefe(ui: UiDaten, katalog: Katalog, umfeld: Umfeld): Befund[] {
  const befunde: Befund[] = [];
  const melder = (ort: string, slot?: number): Melder => (stufe, meldung) => befunde.push({ stufe, ort, meldung, ...(slot === undefined ? {} : { slot }) });
  const quelle = (s: string | undefined): KatalogQuelle | undefined => katalog.quellen.find((q) => q.schluessel === s);
  const n = slotAnzahl(ui.form);
  const rollen = katalog.farbrollen;

  const menueQuelle = quelle(ui.quelle);
  if (ui.quelle && !menueQuelle) melder("UI")("fehler", `Datenquelle „${ui.quelle}“ ist im Katalog nicht bekannt.`);
  const menueBekannt = ui.quelle && !menueQuelle ? null : new Set([...IMMER_BEKANNT, ...(menueQuelle?.menueWerte.map((p) => p.name) ?? [])]);

  pruefeZeilen([ui.titel], [], menueBekannt, melder("Titel"));
  for (const b of zerlege(ui.titel, rollen).befunde) melder("Titel")("warnung", b.meldung);
  pruefeZeilen([], ui.text, menueBekannt, melder("Kopftext"));
  for (const z of ui.text) for (const b of zerlege(z, rollen).befunde) melder("Kopftext")("warnung", b.meldung);

  const listenSlots = new Set(ui.listen.flatMap((l) => l.slots));
  const alleBlaetterSlots = blaetterSlots(ui);
  const blaettern = new Set(alleBlaetterSlots);
  const feste = Object.keys(ui.slots).map(Number).sort((a, b) => a - b);
  for (const i of feste) {
    const slot = ui.slots[i]!;
    const melde = melder(`Slot ${i}`, i);
    if (i >= n) melde("fehler", "Slot liegt außerhalb der Form.");
    if (!slot.symbol) melde("fehler", "Item fehlt.");
    const { zustaende, ...felder } = slot;
    pruefeTexte(felder, menueBekannt, rollen, melde);
    pruefeAktion(felder, katalog, umfeld, melde);
    for (const [name, u] of Object.entries(zustaende ?? {}) as [string, SlotUeberschreibung][]) {
      if (menueQuelle && !menueQuelle.menueZustaende.includes(name)) melde("warnung", `Zustand „${name}“ kennt die Quelle nicht.`);
      else if (!ui.quelle) melde("warnung", `Zustand „${name}“ kennt die Quelle nicht.`);
      pruefeTexte(u, menueBekannt, rollen, melde);
      if (u.aktion !== undefined || u.wert !== undefined) pruefeAktion({ ...felder, ...u }, katalog, umfeld, melde);
    }
  }

  let blaetternde = 0;
  ui.listen.forEach((l: Listenbereich, k) => {
    const melde = melder(`Liste ${l.name}`);
    const q = quelle(l.quelle);
    if (!q) melde("fehler", `Datenquelle „${l.quelle}“ ist im Katalog nicht bekannt.`);
    if (l.slots.length === 0) melde("fehler", "Listenbereich hat keine Slots.");
    if (l.slots.some((i) => i >= n)) melde("fehler", "Slot liegt außerhalb der Form.");
    if (ui.listen.slice(0, k).some((v) => v.slots.some((i) => l.slots.includes(i)))) melde("fehler", "Listenbereiche überlappen sich.");

    const eintragBekannt = !q || !menueBekannt ? null : new Set([...menueBekannt, ...q.eintragWerte.map((p) => p.name)]);
    pruefeTexte(l.vorlage, eintragBekannt, rollen, melde);
    pruefeAktion(l.vorlage, katalog, umfeld, melde);
    for (const [name, u] of Object.entries(l.zustaende ?? {})) {
      if (q && !q.eintragZustaende.includes(name)) melde("warnung", `Zustand „${name}“ kennt die Quelle nicht.`);
      pruefeTexte(u, eintragBekannt, rollen, melde);
      if (u.aktion !== undefined || u.wert !== undefined) pruefeAktion({ ...l.vorlage, ...u }, katalog, umfeld, melde);
    }
    if (l.leer) {
      pruefeTexte(l.leer, menueBekannt, rollen, melde);
      pruefeAktion(l.leer, katalog, umfeld, melde);
    }

    const b = l.blaettern;
    if (b) {
      blaetternde++;
      if (blaetternde > 1) melde("warnung", "Nur ein Listenbereich sollte blättern.");
      for (const eintrag of [b.zurueck, b.weiter, b.info]) {
        if (!eintrag) continue;
        const { slot, ...felder } = eintrag;
        const belegt = slot >= n || listenSlots.has(slot) || ui.slots[slot] !== undefined || alleBlaetterSlots.filter((x) => x === slot).length > 1;
        if (belegt) melder(`Liste ${l.name}`, slot)("fehler", "Blätter-Slot liegt nicht auf einem freien Platz.");
        pruefeTexte(felder, menueBekannt, rollen, melde);
      }
    } else if (q && q.beispiel.eintraege.length > l.slots.length) {
      melde("warnung", "Einträge passen nicht und es fehlt Blättern.");
    }
  });

  const festeKnoepfe = feste.filter((i) => i < n && !blaettern.has(i)).length;
  if (ui.listen.length === 0 && festeKnoepfe > 20) melder("Bedrock")("fehler", "Bedrock zeigt höchstens 20 Knöpfe.");
  if (ui.listen.length > 0 && seitenAufteilung(festeKnoepfe, 100) === null) melder("Bedrock")("fehler", "Zu viele feste Knöpfe neben der Liste für Bedrock.");

  return befunde;
}
